if (!customElements.get('cart-reminder')) {
  customElements.define(
    'cart-reminder',
    class CartReminder extends HTMLElement {
      connectedCallback() {
        // Same move as Dawn's ModalDialog: escape any ancestor that would clip or offset `position: fixed`.
        // Editing settings re-renders the section, so drop the copy left behind on the body.
        if (this.parentElement !== document.body) {
          document.querySelectorAll(`cart-reminder[data-section-id="${this.dataset.sectionId}"]`).forEach((el) => {
            if (el !== this) el.remove();
          });
          document.body.appendChild(this);
        }

        if (this.initialized) return;
        this.initialized = true;

        this.badge = this.querySelector('.cart-reminder__badge');
        this.dialog = this.querySelector('.cart-reminder__dialog');
        this.designMode = Boolean(window.Shopify && window.Shopify.designMode);
        this.storageKey = `${this.dataset.storageKey}:hidden-until`;
        this.initialDelay = Number(this.dataset.initialDelay || 0) * 1000;
        this.reappearDelay = Number(this.dataset.reappearDelay || 60) * 1000;

        this.badge.addEventListener('click', this.open.bind(this));
        this.querySelectorAll('[data-cart-reminder-close]').forEach((element) => {
          element.addEventListener('click', this.close.bind(this));
        });
        this.addEventListener('keyup', (event) => {
          if (event.code.toUpperCase() === 'ESCAPE') this.close();
        });

        if (this.designMode) {
          this.watchThemeEditor();
          this.revealBadge();
          return;
        }

        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate.bind(this));

        // An empty cart waits for something to be added; a cart with items waits out the browse delay.
        if (Number(this.dataset.cartCount) > 0) this.scheduleBadge();
      }

      disconnectedCallback() {
        // Appending to the body disconnects and reconnects this element; only tear down a real removal.
        Promise.resolve().then(() => {
          if (this.isConnected) return;
          clearTimeout(this.badgeTimer);
          this.close();
          if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
          if (this.onSectionSelect) document.removeEventListener('shopify:section:select', this.onSectionSelect);
          if (this.onSectionDeselect) document.removeEventListener('shopify:section:deselect', this.onSectionDeselect);
          this.initialized = false;
        });
      }

      // In the theme editor the popup follows section selection so the merchant can style it.
      watchThemeEditor() {
        this.onSectionSelect = (event) => {
          if (event.detail.sectionId === this.dataset.sectionId) this.open();
        };
        this.onSectionDeselect = (event) => {
          if (event.detail.sectionId === this.dataset.sectionId) this.close();
        };
        document.addEventListener('shopify:section:select', this.onSectionSelect);
        document.addEventListener('shopify:section:deselect', this.onSectionDeselect);
      }

      // Adding to the cart is a deliberate action, so the button shows at once: no browse delay,
      // and any cooldown from a dismissed popup is dropped. Emptying the cart takes it away again.
      onCartUpdate() {
        this.cartHasItems().then((hasItems) => {
          clearTimeout(this.badgeTimer);

          if (!hasItems) {
            this.hideBadge();
            return;
          }

          this.forget();
          this.revealBadge();
        });
      }

      // The cooldown is stored rather than kept in memory so it survives page navigation.
      scheduleBadge() {
        clearTimeout(this.badgeTimer);
        const hiddenUntil = Number(this.readStorage() || 0);
        const wait = Math.max(this.initialDelay, hiddenUntil - Date.now());
        this.badgeTimer = setTimeout(this.showBadge.bind(this), wait);
      }

      showBadge() {
        // Re-checked live: items may have been removed since this page was rendered.
        this.cartHasItems().then((hasItems) => {
          if (hasItems) this.revealBadge();
        });
      }

      revealBadge() {
        // While the popup is open the button is deliberately out of the way.
        if (this.hasAttribute('open')) return;
        this.badge.classList.add('cart-reminder__badge--visible');
      }

      hideBadge() {
        this.badge.classList.remove('cart-reminder__badge--visible');
      }

      cartHasItems() {
        return fetch(`${window.routes.cart_url}.js`)
          .then((response) => response.json())
          .then((cart) => cart.item_count > 0)
          .catch(() => document.querySelector('.cart-count-bubble') !== null);
      }

      open() {
        if (this.hasAttribute('open')) return;
        this.hideBadge();
        this.setAttribute('open', '');
        document.body.classList.add('overflow-hidden');
        trapFocus(this, this.dialog);
      }

      close() {
        if (!this.hasAttribute('open')) return;
        this.removeAttribute('open');
        document.body.classList.remove('overflow-hidden');
        // The button that opened this is now hidden, so there is nothing to hand focus back to.
        removeTrapFocus();

        if (this.designMode) {
          this.revealBadge();
          return;
        }

        this.remember(Date.now() + this.reappearDelay);
        this.scheduleBadge();
      }

      remember(timestamp) {
        try {
          sessionStorage.setItem(this.storageKey, String(timestamp));
        } catch (e) {
          // Storage can be unavailable (private browsing, blocked cookies); the cooldown just resets on navigation.
        }
      }

      forget() {
        try {
          sessionStorage.removeItem(this.storageKey);
        } catch (e) {
          // Nothing to clear when storage is unavailable.
        }
      }

      readStorage() {
        try {
          return sessionStorage.getItem(this.storageKey);
        } catch (e) {
          return null;
        }
      }
    }
  );
}
